'use client';

import { useMemo, useState } from 'react';
import { getStarValue, formatReviewCount } from '../../../_lib/shows/reviewFormat';

interface ReviewsTabProps {
  reviews: {
    averageRating: number;
    totalReviews: number;
    list: Array<{ name: string; rating: number; date: string; comment: string }>;
  } | null;
}

const PAGE_SIZE = 3;
const PREVIEW_LENGTH = 250;

function ReviewCard({ review }: { review: { name: string; rating: number; date: string; comment: string } }) {
  const [expanded, setExpanded] = useState(false);
  const starValue = getStarValue(review.rating);
  const fullStars = Math.floor(starValue);
  const hasHalfStar = starValue % 1 !== 0;
  const isLong = review.comment.length > PREVIEW_LENGTH;
  const dateLabel = new Date(review.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', timeZone: 'UTC' });

  return (
    <div className="shows-detail-review-card shows-detail-card">
      <div className="shows-detail-review-stars">
        {Array.from({ length: 5 }, (_, i) => {
          if (i < fullStars) return <i key={i} className="fas fa-star" />;
          if (i === fullStars && hasHalfStar) return <i key={i} className="fas fa-star-half-alt" />;
          return <i key={i} className="fal fa-star" />;
        })}
      </div>
      <h5 className="shows-detail-review-meta">
        {review.name} <span className='fontsize14'>{dateLabel}</span>
      </h5>
      <p className="shows-detail-review-text">
        {expanded || !isLong ? review.comment : `${review.comment.slice(0, PREVIEW_LENGTH)}...`}
        {isLong && (
          <button type="button" className="shows-detail-toggle-link" onClick={() => setExpanded((v) => !v)}>
            {' '}
            {expanded ? 'Read less' : 'Read more'}
          </button>
        )}
      </p>
    </div>
  );
}

/**
 * Reviews tab — brd-frontend.md §6.4, brd-backend.md §6.2, frd.md
 * FR-TAB-05..07. Never hidden (unlike Info/Schedule/Accessibility) — a
 * zero-review show still renders the tab with "No reviews yet" and no
 * pagination. Reuses the shared `getStarValue`/`formatReviewCount` (see
 * `ShowDetailHeader`'s doc comment for why).
 */
export function ReviewsTab({ reviews }: ReviewsTabProps) {
  const [page, setPage] = useState(1);

  const sorted = useMemo(
    () => [...(reviews?.list || [])].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [reviews],
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageItems = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const hasRating = reviews != null && reviews.averageRating > 0;
  const starValue = hasRating ? getStarValue(reviews!.averageRating) : 0;
  const fullStars = Math.floor(starValue);
  const hasHalfStar = starValue % 1 !== 0;

  return (
    <div className="shows-detail-tab-panel shows-detail-reviews-panel">
      <div className="shows-detail-reviews-heading">
        <h4>Reviews</h4>
        {sorted.length > 0 && (
          <div className="shows-detail-reviews-rating">
            {hasRating && (
              <div className="rating-top">
                <span className="shows-detail-header-rating">{reviews!.averageRating.toFixed(1)}</span>
                <span className="shows-detail-header-stars">
                  {Array.from({ length: 5 }, (_, i) => {
                    if (i < fullStars) return <i key={i} className="fas fa-star" />;
                    if (i === fullStars && hasHalfStar) return <i key={i} className="fas fa-star-half-alt" />;
                    return <i key={i} className="fal fa-star" />;
                  })}
                </span>
              </div>
            )}
            <small className='text-gray-text text-15 text-right'>{formatReviewCount(reviews!.totalReviews)}</small>
          </div>
        )}
      </div>

      {sorted.length === 0 ? (
        <div className="shows-detail-offer-sec">
          <p className="shows-detail-no-reviews">No reviews yet</p>
        </div>
      ) : (
        <>
          {pageItems.map((r, i) => (
            <ReviewCard key={`${r.name}-${r.date}-${i}`} review={r} />
          ))}

          {totalPages > 1 && (
            <nav className="shows-detail-pagination" aria-label="Reviews pagination">
              <button type="button" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                {/* <i className="fa-regular fa-chevron-left" aria-hidden />  */} Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  className={n === page ? 'is-active' : ''}
                  onClick={() => setPage(n)}
                >
                  {n}
                </button>
              ))}
              <button type="button" disabled={page === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                {/* Next <i className="fa-regular fa-chevron-right" aria-hidden /> */} Next
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}
